import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, query, where, serverTimestamp, writeBatch } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyCq5MyEwXpDPX8K-kweTHgqq8tX9hri_ZY",
  authDomain: "scalovate-website.firebaseapp.com",
  projectId: "scalovate-website",
  storageBucket: "scalovate-website.firebasestorage.app",
  messagingSenderId: "522313075961",
  appId: "1:522313075961:web:d3814fa4a5b656344a2628"
};

// Initialize Firebase
let app = null;
let db = null;
let storage = null;
let auth = null;

try {
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  storage = getStorage(app);
  auth = getAuth(app);
  
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Firebase initialization error:', error);
}

// Test Firebase connection
export const testFirebaseConnection = async () => {
  try {
    if (!db) {
      throw new Error('Firestore not initialized');
    }
    
    // Try to read from a test collection
    const testDoc = doc(db, 'test', 'connection');
    await getDoc(testDoc);
    
    console.log('Firebase connection successful');
    return true;
  } catch (error) {
    console.error('Firebase connection failed:', error);
    return false;
  }
};

// Check if Firebase is available
export const isFirebaseAvailable = () => {
  return !!(app && db && storage && auth);
};

// Initialize default content only once
export const initializeDefaultContent = async () => {
  try {
    if (!db) return false;
    
    // Check if content has already been initialized
    const initDoc = doc(db, 'system', 'initialized');
    const initSnapshot = await getDoc(initDoc);
    
    if (initSnapshot.exists()) {
      console.log('Default content already initialized');
      return true;
    }
    
    // Add default content only if not already present
    const defaultContent = [
      {
        id: 'ai-solutions',
        subject: 'AI Solutions',
        details: 'Scalovate provides comprehensive AI solutions including machine learning, natural language processing, and computer vision technologies.',
        category: 'services',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 'automation-framework',
        subject: 'Automation Framework',
        details: 'Our proprietary automation framework leverages private AI and MCP to connect multiple systems seamlessly.',
        category: 'products',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 'ai-security',
        subject: 'AI Security',
        details: 'Advanced AI security solutions and awareness training to protect your organization from emerging threats.',
        category: 'services',
        isActive: true,
        createdAt: new Date()
      }
    ];
    
    // Add default content
    for (const content of defaultContent) {
      await setDoc(doc(db, 'content', content.id), content);
    }
    
    // Mark as initialized
    await setDoc(initDoc, { 
      initialized: true, 
      timestamp: new Date(),
      version: '1.0'
    });
    
    console.log('Default content initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing default content:', error);
    return false;
  }
};

// Get system settings
export const getSystemSettings = async () => {
  try {
    const settingsDoc = doc(db, 'system', 'settings');
    const snapshot = await getDoc(settingsDoc);
    
    if (snapshot.exists()) {
      return snapshot.data();
    }
    
    // Return default settings if none exist
    const defaultSettings = {
      theme: {
        primaryColor: '#ce6161',
        secondaryColor: '#000000',
        darkMode: false
      },
      branding: {
        logoUrl: '/scalovate_logo.png',
        darkLogoUrl: '',
        headerText: 'Paradigm Shift in Technology',
        footerText: 'Scalovate - AI Solutions & Enterprise Intelligence',
        copyright: '© 2025 Scalovate. All rights reserved.',
        locations: 'Qatar • Australia • Canada',
        headerBackgroundColor: '#808080',
        headerTextColor: '#000000',
        headerTransparency: 80,
        chatHeaderBackgroundColor: '#f3f4f6',
        chatHeaderTextColor: '#000000',
        chatHeaderTransparency: 70,
        footerBackgroundColor: '#808080',
        footerTextColor: '#ffffff',
        footerTransparency: 80,
        navigationLinks: [
          { name: 'Home', url: '/', active: true },
          { name: 'Services', url: '/services', active: true },
          { name: 'About', url: '/about', active: true },
          { name: 'Contact', url: '/contact', active: true }
        ],
        socialMediaLinks: [
          { name: 'LinkedIn', url: 'https://linkedin.com/company/scalovate', icon: 'linkedin', active: true },
          { name: 'YouTube', url: 'https://youtube.com/@scalovate', icon: 'youtube', active: true },
          { name: 'Facebook', url: 'https://facebook.com/scalovate', icon: 'facebook', active: true },
          { name: 'Instagram', url: 'https://instagram.com/scalovate', icon: 'instagram', active: true },
          { name: 'Twitter', url: 'https://twitter.com/scalovate', icon: 'twitter', active: false }
        ]
      },
      aiAssistant: {
        name: 'Jeje',
        avatarUrl: '/jeje_bot.png',
        description: 'Senior AI Advisory Consultant at Scalovate',
        personality: 'Mature, professional advisory consultant specializing in AI adoption and business transformation',
        welcomeMessage: 'Hello, I\'m Jeje, a Senior AI Advisory Consultant at Scalovate. I help organizations navigate AI adoption and digital transformation strategies. How can I assist you with your AI initiatives today?',
        communicationGuidelines: 'Maintain a mature, consultative approach. Speak as an experienced business advisor. Use professional language without being overly formal. Demonstrate deep understanding of business challenges and provide thoughtful, strategic insights.',
        responseMaturity: 'generic'
      },
      actionButtons: [
        { text: 'AI Solutions', key: 'ai-solutions', active: true },
        { text: 'Products', key: 'products', active: true },
        { text: 'About', key: 'about', active: true },
        { text: 'PrivateAI', key: 'private-ai', active: true },
        { text: 'AI Usecases', key: 'ai-usecases', active: true },
        { text: 'Contact', key: 'contact', active: true }
      ],
      userPermissions: {
        allowChatDeletion: true,
        allowProfileEdit: true,
        allowPasswordChange: true
      },
      aiModels: {
        ollama: { enabled: true, apiUrl: 'http://localhost:11434' },
        openai: { enabled: false, apiKey: '' },
        groq: { enabled: false, apiKey: '' },
        claude: { enabled: false, apiKey: '' },
        gemini: { enabled: false, apiKey: '' }
      }
    };
    
    await setDoc(settingsDoc, defaultSettings);
    return defaultSettings;
  } catch (error) {
    console.error('Error getting system settings:', error);
    return null;
  }
};

// Update system settings
export const updateSystemSettings = async (settings) => {
  try {
    const settingsDoc = doc(db, 'system', 'settings');
    await setDoc(settingsDoc, settings, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating system settings:', error);
    return false;
  }
};

// Admin user management functions
export const getAdminUsers = async () => {
  try {
    console.log('🔍 Attempting to get admin users from Firebase...');
    const adminDoc = doc(db, 'system', 'admins');
    const snapshot = await getDoc(adminDoc);
    
    if (snapshot.exists()) {
      console.log('✅ Admin users found in Firebase');
      return snapshot.data().users || [];
    }
    
    console.log('⚠️ No admin users found, initializing default admin...');
    // Initialize with default admin if none exist
    const defaultAdmins = [
      {
        id: 'admin-1',
        email: 'user-support@scalovate.com',
        password: 'iamgettingOldn0w', // In production, this should be hashed
        role: 'super-admin',
        name: 'Super Admin',
        createdAt: new Date().toISOString(),
        active: true
      }
    ];
    
    try {
      await setDoc(adminDoc, { users: defaultAdmins });
      console.log('✅ Default admin initialized successfully');
      return defaultAdmins;
    } catch (writeError) {
      console.warn('⚠️ Could not write to Firebase, using local default admin:', writeError.message);
      // Return default admin even if we can't write to Firebase
      return defaultAdmins;
    }
  } catch (error) {
    console.error('❌ Error getting admin users:', error);
    // Return default admin as fallback
    return [
      {
        id: 'admin-1',
        email: 'user-support@scalovate.com',
        password: 'iamgettingOldn0w',
        role: 'super-admin',
        name: 'Super Admin',
        createdAt: new Date().toISOString(),
        active: true
      }
    ];
  }
};

export const addAdminUser = async (adminData) => {
  try {
    console.log('🔍 Attempting to add admin user to Firebase...', adminData.email);
    
    // First, try to get existing users
    let users = [];
    try {
      const adminDoc = doc(db, 'system', 'admins');
      const snapshot = await getDoc(adminDoc);
      
      if (snapshot.exists()) {
        users = snapshot.data().users || [];
      }
    } catch (readError) {
      console.warn('⚠️ Could not read existing admins, starting with empty array:', readError.message);
      // If we can't read, start with empty array and try to write
      users = [];
    }
    
    // Check if email already exists
    if (users.some(user => user.email === adminData.email)) {
      throw new Error('Admin with this email already exists');
    }
    
    const newAdmin = {
      id: `admin-${Date.now()}`,
      ...adminData,
      createdAt: new Date().toISOString(),
      active: true
    };
    
    users.push(newAdmin);
    
    try {
      const adminDoc = doc(db, 'system', 'admins');
      await setDoc(adminDoc, { users }, { merge: true });
      console.log('✅ Admin user added successfully to Firebase');
      return newAdmin;
    } catch (writeError) {
      console.error('❌ Firebase write failed:', writeError);
      
      // Check if it's a permission error
      if (writeError.code === 'permission-denied') {
        throw new Error('Permission denied: Please check Firebase security rules. For development, you may need to allow read/write access to the "system" collection.');
      } else if (writeError.code === 'unavailable') {
        throw new Error('Firebase is currently unavailable. Please try again later.');
      } else {
        throw new Error(`Firebase error: ${writeError.message}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Error adding admin user:', error);
    throw error;
  }
};

export const updateAdminUser = async (adminId, updateData) => {
  try {
    console.log('🔍 Attempting to update admin user in Firebase...', adminId);
    
    const adminDoc = doc(db, 'system', 'admins');
    const snapshot = await getDoc(adminDoc);
    
    if (!snapshot.exists()) {
      throw new Error('No admin users found');
    }
    
    const users = snapshot.data().users || [];
    const userIndex = users.findIndex(user => user.id === adminId);
    
    if (userIndex === -1) {
      throw new Error('Admin user not found');
    }
    
    users[userIndex] = { ...users[userIndex], ...updateData, updatedAt: new Date().toISOString() };
    
    try {
      await setDoc(adminDoc, { users }, { merge: true });
      console.log('✅ Admin user updated successfully in Firebase');
      return users[userIndex];
    } catch (writeError) {
      console.error('❌ Firebase write failed:', writeError);
      
      if (writeError.code === 'permission-denied') {
        throw new Error('Permission denied: Please check Firebase security rules.');
      } else {
        throw new Error(`Firebase error: ${writeError.message}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Error updating admin user:', error);
    throw error;
  }
};

export const deleteAdminUser = async (adminId) => {
  try {
    console.log('🔍 Attempting to delete admin user from Firebase...', adminId);
    
    const adminDoc = doc(db, 'system', 'admins');
    const snapshot = await getDoc(adminDoc);
    
    if (!snapshot.exists()) {
      throw new Error('No admin users found');
    }
    
    const users = snapshot.data().users || [];
    const filteredUsers = users.filter(user => user.id !== adminId);
    
    if (filteredUsers.length === users.length) {
      throw new Error('Admin user not found');
    }
    
    // Prevent deleting the last admin
    if (filteredUsers.length === 0) {
      throw new Error('Cannot delete the last admin user');
    }
    
    try {
      await setDoc(adminDoc, { users: filteredUsers }, { merge: true });
      console.log('✅ Admin user deleted successfully from Firebase');
      return true;
    } catch (writeError) {
      console.error('❌ Firebase write failed:', writeError);
      
      if (writeError.code === 'permission-denied') {
        throw new Error('Permission denied: Please check Firebase security rules.');
      } else {
        throw new Error(`Firebase error: ${writeError.message}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Error deleting admin user:', error);
    throw error;
  }
};

// Debug function to check admin users
export const debugAdminUsers = async () => {
  try {
    console.log('🔧 DEBUG: Checking admin users in Firebase...');
    
    const adminDoc = doc(db, 'system', 'admins');
    const snapshot = await getDoc(adminDoc);
    
    console.log('📋 Firebase document exists:', snapshot.exists());
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      console.log('📋 Raw document data:', data);
      console.log('📋 Users array:', data.users);
      
      if (data.users && Array.isArray(data.users)) {
        console.log('📋 Number of admin users:', data.users.length);
        data.users.forEach((user, index) => {
          console.log(`📋 Admin ${index + 1}:`, {
            id: user.id,
            email: user.email,
            name: user.name,
            role: user.role,
            active: user.active,
            hasPassword: !!user.password
          });
        });
      } else {
        console.log('⚠️ No users array found in document');
      }
    } else {
      console.log('⚠️ Admin document does not exist');
    }
    
    return { success: true };
  } catch (error) {
    console.error('❌ Debug admin users error:', error);
    return { success: false, error: error.message };
  }
};

export const validateAdminLogin = async (email, password) => {
  try {
    console.log('🔍 Validating admin login for:', email);
    
    // First, debug the admin users
    await debugAdminUsers();
    
    const adminUsers = await getAdminUsers();
    console.log('📋 Retrieved admin users:', adminUsers.length);
    
    // Check each condition separately for better debugging
    const emailMatch = adminUsers.filter(user => user.email === email);
    console.log('📋 Users with matching email:', emailMatch.length);
    
    if (emailMatch.length === 0) {
      console.log('❌ No user found with email:', email);
      return { success: false, message: `No admin user found with email: ${email}` };
    }
    
    const passwordMatch = emailMatch.filter(user => user.password === password);
    console.log('📋 Users with matching password:', passwordMatch.length);
    
    if (passwordMatch.length === 0) {
      console.log('❌ Password does not match for email:', email);
      return { success: false, message: 'Invalid password' };
    }
    
    const activeMatch = passwordMatch.filter(user => user.active);
    console.log('📋 Active users with matching credentials:', activeMatch.length);
    
    if (activeMatch.length === 0) {
      console.log('❌ User account is not active for email:', email);
      return { success: false, message: 'Account is not active' };
    }
    
    const admin = activeMatch[0];
    console.log('✅ Admin login successful for:', admin.name);
    
    // Update last login
    try {
      await updateAdminUser(admin.id, { lastLogin: new Date().toISOString() });
      console.log('✅ Last login updated');
    } catch (updateError) {
      console.warn('⚠️ Could not update last login:', updateError.message);
      // Don't fail login if we can't update last login
    }
    
    return { success: true, admin };
    
  } catch (error) {
    console.error('❌ Error validating admin login:', error);
    return { success: false, message: `Login validation failed: ${error.message}` };
  }
};

// Password reset functions
export const generatePasswordResetToken = () => {
  // Generate a secure random token
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

export const createPasswordResetRequest = async (email) => {
  try {
    console.log('🔍 Creating password reset request for:', email);
    
    // First, debug the admin users
    await debugAdminUsers();
    
    // Check if admin user exists
    const adminUsers = await getAdminUsers();
    console.log('📋 Retrieved admin users for reset:', adminUsers.length);
    
    // Check each condition separately for better debugging
    const emailMatch = adminUsers.filter(user => user.email === email);
    console.log('📋 Users with matching email for reset:', emailMatch.length);
    
    if (emailMatch.length === 0) {
      console.log('❌ No user found with email for reset:', email);
      throw new Error(`No admin user found with email: ${email}`);
    }
    
    const activeMatch = emailMatch.filter(user => user.active);
    console.log('📋 Active users with matching email for reset:', activeMatch.length);
    
    if (activeMatch.length === 0) {
      console.log('❌ User account is not active for reset:', email);
      throw new Error('Admin account is not active');
    }
    
    const admin = activeMatch[0];
    console.log('✅ Admin found for password reset:', admin.name);
    
    // Generate reset token
    const resetToken = generatePasswordResetToken();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now
    
    // Store reset token in Firebase
    const resetDoc = doc(db, 'system', 'passwordResets');
    const resetData = {
      [resetToken]: {
        email: email,
        adminId: admin.id,
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
        used: false
      }
    };
    
    await setDoc(resetDoc, resetData, { merge: true });
    
    console.log('✅ Password reset token created successfully');
    return { success: true, resetToken, expiresAt };
    
  } catch (error) {
    console.error('❌ Error creating password reset request:', error);
    throw error;
  }
};

export const validatePasswordResetToken = async (token) => {
  try {
    console.log('🔍 Validating password reset token...');
    
    const resetDoc = doc(db, 'system', 'passwordResets');
    const snapshot = await getDoc(resetDoc);
    
    if (!snapshot.exists()) {
      throw new Error('Invalid reset token');
    }
    
    const resetData = snapshot.data()[token];
    
    if (!resetData) {
      throw new Error('Invalid reset token');
    }
    
    if (resetData.used) {
      throw new Error('Reset token has already been used');
    }
    
    const expiresAt = new Date(resetData.expiresAt);
    if (new Date() > expiresAt) {
      throw new Error('Reset token has expired');
    }
    
    console.log('✅ Password reset token is valid');
    return { success: true, resetData };
    
  } catch (error) {
    console.error('❌ Error validating reset token:', error);
    throw error;
  }
};

export const resetAdminPassword = async (token, newPassword) => {
  try {
    console.log('🔍 Resetting admin password...');
    
    // Validate token first
    const validation = await validatePasswordResetToken(token);
    if (!validation.success) {
      throw new Error('Invalid or expired reset token');
    }
    
    const { resetData } = validation;
    
    // Update admin password
    await updateAdminUser(resetData.adminId, { 
      password: newPassword,
      passwordResetAt: new Date().toISOString()
    });
    
    // Mark token as used
    const resetDoc = doc(db, 'system', 'passwordResets');
    const updateData = {
      [token]: {
        ...resetData,
        used: true,
        usedAt: new Date().toISOString()
      }
    };
    
    await setDoc(resetDoc, updateData, { merge: true });
    
    console.log('✅ Admin password reset successfully');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error resetting admin password:', error);
    throw error;
  }
};

export const sendPasswordResetEmail = async (email, resetToken) => {
  try {
    // In a real application, you would integrate with an email service like:
    // - SendGrid
    // - AWS SES
    // - Nodemailer with SMTP
    // - Firebase Functions with email service
    
    // For now, we'll simulate the email sending and provide the reset link
    const resetLink = `${window.location.origin}/admin/reset-password?token=${resetToken}`;
    
    console.log('📧 Password reset email would be sent to:', email);
    console.log('🔗 Reset link:', resetLink);
    
    // In production, replace this with actual email sending logic
    // Example with a hypothetical email service:
    /*
    await emailService.send({
      to: email,
      subject: 'Scalovate Admin - Password Reset Request',
      html: `
        <h2>Password Reset Request</h2>
        <p>You have requested to reset your admin password for Scalovate.</p>
        <p>Click the link below to reset your password:</p>
        <a href="${resetLink}" style="background: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Reset Password</a>
        <p>This link will expire in 24 hours.</p>
        <p>If you didn't request this reset, please ignore this email.</p>
      `
    });
    */
    
    return { 
      success: true, 
      message: 'Password reset instructions have been sent to your email',
      resetLink // Remove this in production
    };
    
  } catch (error) {
    console.error('❌ Error sending password reset email:', error);
    throw error;
  }
};

export const cleanupExpiredResetTokens = async () => {
  try {
    console.log('🧹 Cleaning up expired reset tokens...');
    
    const resetDoc = doc(db, 'system', 'passwordResets');
    const snapshot = await getDoc(resetDoc);
    
    if (!snapshot.exists()) {
      return { success: true, cleaned: 0 };
    }
    
    const resetData = snapshot.data();
    const now = new Date();
    let cleanedCount = 0;
    const updatedData = {};
    
    Object.entries(resetData).forEach(([token, data]) => {
      const expiresAt = new Date(data.expiresAt);
      if (now <= expiresAt) {
        // Keep non-expired tokens
        updatedData[token] = data;
      } else {
        cleanedCount++;
      }
    });
    
    if (cleanedCount > 0) {
      await setDoc(resetDoc, updatedData);
      console.log(`✅ Cleaned up ${cleanedCount} expired reset tokens`);
    }
    
    return { success: true, cleaned: cleanedCount };
    
  } catch (error) {
    console.error('❌ Error cleaning up expired tokens:', error);
    throw error;
  }
};

// Force initialize admin user (for troubleshooting)
export const forceInitializeAdminUser = async () => {
  try {
    console.log('🔧 Force initializing admin user...');
    
    const defaultAdmin = {
      id: 'admin-1',
      email: 'user-support@scalovate.com',
      password: 'iamgettingOldn0w',
      role: 'super-admin',
      name: 'Super Admin',
      createdAt: new Date().toISOString(),
      active: true
    };
    
    const adminDoc = doc(db, 'system', 'admins');
    await setDoc(adminDoc, { users: [defaultAdmin] }, { merge: false });
    
    console.log('✅ Admin user force initialized successfully');
    return { success: true, admin: defaultAdmin };
    
  } catch (error) {
    console.error('❌ Error force initializing admin user:', error);
    throw error;
  }
};

/**
 * Enhanced Event Logging with File Location Tracking
 * Logs every single activity in the application with detailed metadata
 */
export const logEvent = async (type, action, details, metadata = {}, userId = null, userEmail = null) => {
  try {
    // Get current user info if not provided
    if (!userId || !userEmail) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        userId = currentUser.uid;
        userEmail = currentUser.email;
      }
    }

    // Get caller information for file tracking
    const stack = new Error().stack;
    const callerInfo = extractCallerInfo(stack);

    // Enhanced event log structure
    const eventLog = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      action,
      details,
      timestamp: serverTimestamp(),
      userId: userId || 'anonymous',
      userEmail: userEmail || 'anonymous@system.com',
      userRole: metadata.userRole || 'User',
      
      // File and location tracking
      sourceFile: callerInfo.file || 'unknown',
      sourceFunction: callerInfo.function || 'unknown',
      sourceLine: callerInfo.line || 'unknown',
      
      // Enhanced metadata
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        sessionId: getSessionId(),
        url: window.location.href,
        referrer: document.referrer,
        screenResolution: getScreenResolution(),
        browserLanguage: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      
      // Network information
      ipAddress: await getClientIP(),
      
      // Performance metrics
      performanceMetrics: {
        memoryUsage: getMemoryUsage(),
        connectionType: getConnectionType(),
        loadTime: performance.now()
      }
    };

    // Save to Firebase
    const eventRef = doc(db, 'eventLogs', eventLog.id);
    await setDoc(eventRef, eventLog);

    console.log(`📝 Event logged: ${type}/${action} from ${callerInfo.file}:${callerInfo.line}`);
    
    return eventLog.id;
  } catch (error) {
    console.error('❌ Failed to log event:', error);
    // Don't throw error to avoid breaking app functionality
    return null;
  }
};

/**
 * Extract caller information from stack trace
 */
const extractCallerInfo = (stack) => {
  try {
    const lines = stack.split('\n');
    // Skip the first few lines (Error, logEvent, etc.)
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for React component or service file
      if (line.includes('.js') || line.includes('.jsx') || line.includes('.ts') || line.includes('.tsx')) {
        const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
        if (match) {
          const [, functionName, filePath, lineNumber] = match;
          const fileName = filePath.split('/').pop() || filePath.split('\\').pop();
          
          return {
            function: functionName || 'anonymous',
            file: fileName,
            line: lineNumber,
            fullPath: filePath
          };
        }
        
        // Alternative format
        const altMatch = line.match(/at\s+(.+?):(\d+):(\d+)/);
        if (altMatch) {
          const [, filePath, lineNumber] = altMatch;
          const fileName = filePath.split('/').pop() || filePath.split('\\').pop();
          
          return {
            function: 'anonymous',
            file: fileName,
            line: lineNumber,
            fullPath: filePath
          };
        }
      }
    }
    
    return { function: 'unknown', file: 'unknown', line: 'unknown' };
  } catch (error) {
    return { function: 'unknown', file: 'unknown', line: 'unknown' };
  }
};

/**
 * Get or create session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('scalovate_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('scalovate_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get client IP address (best effort)
 */
const getClientIP = async () => {
  try {
    // In production, you might want to use a service like ipapi.co
    // For now, return a placeholder
    return 'client_ip_placeholder';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Get memory usage information
 */
const getMemoryUsage = () => {
  try {
    if (performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Get connection type
 */
const getConnectionType = () => {
  try {
    if (navigator.connection) {
      return {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Get screen resolution safely
 */
const getScreenResolution = () => {
  try {
    if (typeof window !== 'undefined' && window.screen) {
      return `${window.screen.width}x${window.screen.height}`;
    }
    return 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Specialized logging functions for different activity types
 */

// Authentication logging
export const logAuthEvent = async (action, details, userId = null, userEmail = null, userRole = 'User') => {
  return await logEvent('authentication', action, details, { userRole }, userId, userEmail);
};

// Admin activity logging
export const logAdminEvent = async (action, details, adminId = null, adminEmail = null, adminRole = 'Admin') => {
  return await logEvent('admin_activity', action, details, { userRole: adminRole }, adminId, adminEmail);
};

// UI/Settings changes logging
export const logUIEvent = async (action, details, changes = {}) => {
  return await logEvent('ui_changes', action, details, { changes });
};

// Content management logging
export const logContentEvent = async (action, details, contentId = null, contentType = null) => {
  return await logEvent('content_management', action, details, { contentId, contentType });
};

// Chat activity logging
export const logChatEvent = async (action, details, chatId = null, messageCount = null) => {
  return await logEvent('chat_activity', action, details, { chatId, messageCount });
};

// Profile/User management logging
export const logProfileEvent = async (action, details, targetUserId = null, changes = {}) => {
  return await logEvent('profile_management', action, details, { targetUserId, changes });
};

// System/Database logging
export const logSystemEvent = async (action, details, affectedCollection = null, documentId = null) => {
  return await logEvent('system_activity', action, details, { affectedCollection, documentId });
};

// Error logging
export const logErrorEvent = async (error, context = '', additionalInfo = {}) => {
  const errorDetails = {
    message: error.message,
    stack: error.stack,
    context,
    ...additionalInfo
  };
  
  return await logEvent('error', 'application_error', `Error in ${context}: ${error.message}`, errorDetails);
};

// Performance logging
export const logPerformanceEvent = async (action, details, metrics = {}) => {
  return await logEvent('performance', action, details, { metrics });
};

/**
 * Batch logging for multiple events
 */
export const logBatchEvents = async (events) => {
  try {
    const batch = writeBatch(db);
    const eventIds = [];
    
    for (const event of events) {
      const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const eventRef = doc(db, 'eventLogs', eventId);
      
      const enhancedEvent = {
        ...event,
        id: eventId,
        timestamp: serverTimestamp(),
        metadata: {
          ...event.metadata,
          batchLogged: true,
          batchSize: events.length
        }
      };
      
      batch.set(eventRef, enhancedEvent);
      eventIds.push(eventId);
    }
    
    await batch.commit();
    console.log(`📝 Batch logged ${events.length} events`);
    
    return eventIds;
  } catch (error) {
    console.error('❌ Failed to batch log events:', error);
    return [];
  }
};

export const getEventLogs = async (daysBack = 90) => {
  try {
    if (!db) {
      console.log('❌ Database not initialized');
      return [];
    }
    
    console.log(`🔍 Querying eventLogs collection...`);
    
    const logsCollection = collection(db, 'eventLogs');
    
    // First, try to get all documents to see if any exist
    let snapshot = await getDocs(logsCollection);
    console.log(`📊 Found ${snapshot.size} total documents in eventLogs collection`);
    
    if (snapshot.size === 0) {
      console.log('📭 No documents found in eventLogs collection');
      return [];
    }
    
    // If we have documents, apply the date filter
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);
    console.log(`🔍 Filtering for events after ${cutoffDate.toISOString()}`);
    
    const logs = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      console.log(`📄 Document ${doc.id}:`, data);
      
      // Apply date filter manually since Firestore query might have issues
      let includeLog = true;
      if (data.timestamp) {
        // Handle both Firestore timestamp and ISO string formats
        let logDate;
        if (data.timestamp.toDate) {
          // Firestore timestamp
          logDate = data.timestamp.toDate();
        } else if (typeof data.timestamp === 'string') {
          // ISO string
          logDate = new Date(data.timestamp);
        } else {
          // Fallback
          logDate = new Date(data.timestamp);
        }
        includeLog = logDate >= cutoffDate;
      }
      
      if (includeLog) {
        // Convert timestamp to ISO string for consistent handling
        const processedData = { ...data };
        if (data.timestamp && data.timestamp.toDate) {
          processedData.timestamp = data.timestamp.toDate().toISOString();
        }
        logs.push({ id: doc.id, ...processedData });
      }
    });
    
    const sortedLogs = logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    console.log(`✅ Returning ${sortedLogs.length} filtered and sorted event logs`);
    return sortedLogs;
  } catch (error) {
    console.error('❌ Error getting event logs:', error);
    return [];
  }
};

export const deleteOldEventLogs = async () => {
  try {
    if (!db) return false;
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 90);
    const cutoffDateString = cutoffDate.toISOString();
    
    const logsCollection = collection(db, 'eventLogs');
    const q = query(logsCollection, where('timestamp', '<', cutoffDateString));
    const snapshot = await getDocs(q);
    
    const batch = [];
    snapshot.forEach((doc) => {
      batch.push(doc.ref);
    });
    
    // Delete in batches of 500 (Firestore limit)
    for (let i = 0; i < batch.length; i += 500) {
      const batchSlice = batch.slice(i, i + 500);
      await Promise.all(batchSlice.map(ref => ref.delete()));
    }
    
    console.log(`Deleted ${batch.length} old event logs`);
    return true;
  } catch (error) {
    console.error('Error deleting old event logs:', error);
    return false;
  }
};

export const getEventLogsSummary = async (daysBack = 30) => {
  try {
    const logs = await getEventLogs(daysBack);
    
    const summary = {
      totalEvents: logs.length,
      eventsByType: {},
      eventsByUser: {},
      eventsByDate: {},
      recentEvents: logs.slice(0, 10)
    };
    
    logs.forEach(log => {
      // Count by event type (fix field name mismatch)
      const eventType = log.type || log.eventType || 'Unknown';
      summary.eventsByType[eventType] = (summary.eventsByType[eventType] || 0) + 1;
      
      // Count by user
      const user = log.userEmail || log.userId || 'System';
      summary.eventsByUser[user] = (summary.eventsByUser[user] || 0) + 1;
      
      // Count by date
      const date = new Date(log.timestamp).toDateString();
      summary.eventsByDate[date] = (summary.eventsByDate[date] || 0) + 1;
    });
    
    return summary;
  } catch (error) {
    console.error('Error getting event logs summary:', error);
    return {
      totalEvents: 0,
      eventsByType: {},
      eventsByUser: {},
      eventsByDate: {},
      recentEvents: []
    };
  }
};

/**
 * Generate test event logs for debugging
 */
export const generateTestEventLogs = async () => {
  try {
    console.log('🧪 Generating test event logs...');
    
    const testEvents = [
      {
        type: 'authentication',
        action: 'admin_login',
        details: 'Super Admin logged in successfully',
        metadata: { userRole: 'Super Admin', loginMethod: 'email_password' }
      },
      {
        type: 'admin_activity',
        action: 'create_user',
        details: 'New admin user created',
        metadata: { userRole: 'Admin', targetUser: 'test@example.com' }
      },
      {
        type: 'ui_changes',
        action: 'color_update',
        details: 'Header color changed',
        metadata: { changes: { headerColor: '#ff0000', previousColor: '#000000' } }
      },
      {
        type: 'content_management',
        action: 'create_content',
        details: 'New content item added',
        metadata: { contentId: 'test-content-001', contentType: 'article' }
      },
      {
        type: 'chat_activity',
        action: 'new_session',
        details: 'Chat session started',
        metadata: { chatId: 'chat-001', messageCount: 0 }
      },
      {
        type: 'system_activity',
        action: 'database_update',
        details: 'Database schema updated',
        metadata: { affectedCollection: 'users', documentId: 'schema-v1' }
      }
    ];

    const eventIds = [];
    for (const event of testEvents) {
      const eventId = await logEvent(
        event.type,
        event.action,
        event.details,
        event.metadata,
        'test-user-id',
        'test@scalovate.com'
      );
      if (eventId) {
        eventIds.push(eventId);
      }
      // Small delay between events
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log(`✅ Generated ${eventIds.length} test event logs`);
    return eventIds;
  } catch (error) {
    console.error('❌ Failed to generate test event logs:', error);
    return [];
  }
};

export { db, storage, auth, app }; 